/**
 * Repo Edit Service — Agentic "Branch → Edit → Diff → Commit → PR" workflow.
 * Parses AI-suggested code changes and pushes them to GitHub safely via a branch + PR.
 */

const API_BASE = '';

export interface AIFileEdit {
  /** "owner/name" — required when multiple repos are attached */
  repo?: string;
  file_path: string;
  content: string;
  description: string;
}

export interface AIEditBlock {
  files: AIFileEdit[];
  rawMatch: string;
}

export interface CommitResult {
  commitSha: string;
  commitUrl: string;
  branch: string;
  filesChanged: number;
}

export interface PRResult {
  prNumber: number;
  prUrl: string;
  title: string;
  state: string;
}

export type PushMode = 'new_branch_pr' | 'existing_branch' | 'commit_only';

export interface PushBranchOptions {
  mode: PushMode;
  /** Branch to commit to (new or existing). */
  headBranch: string;
  /** Fork point when creating a new branch. */
  baseBranch?: string;
  /** PR merge target branch. */
  prBase?: string;
  /** For existing_branch mode — open a PR after commit. */
  openPr?: boolean;
}

export interface PushResult {
  branch: string;
  commit: CommitResult;
  pr?: PRResult;
}

// ── Auth ─────────────────────────────────────────────────────────────────────

async function getAuthHeaders(): Promise<HeadersInit> {
  const { getCurrentSession } = await import('@/lib/supabase');
  const session = await getCurrentSession();
  if (!session) throw new Error('Not authenticated');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`,
  };
}

// ── Parse AI edit block ───────────────────────────────────────────────────────

/** Extracts %%REPO_EDIT%% JSON block from an AI message. Returns null if not found. */
export function parseAIEditBlock(aiMessage: string): AIEditBlock | null {
  const match = aiMessage.match(/%%REPO_EDIT%%([\s\S]*?)%%END_REPO_EDIT%%/);
  if (!match) return null;
  try {
    const files = JSON.parse(match[1].trim()) as AIFileEdit[];
    if (!Array.isArray(files) || files.length === 0) return null;
    if (!files.every(f => typeof f.file_path === 'string' && typeof f.content === 'string')) return null;
    return { files, rawMatch: match[0] };
  } catch {
    return null;
  }
}

/** Returns true if message contains an AI edit block. */
export function hasAIEditBlock(message: string): boolean {
  return message.includes('%%REPO_EDIT%%') && message.includes('%%END_REPO_EDIT%%');
}

/** Returns message with the %%REPO_EDIT%%...%%END_REPO_EDIT%% block removed. */
export function stripEditBlock(message: string): string {
  return message.replace(/%%REPO_EDIT%%[\s\S]*?%%END_REPO_EDIT%%/g, '').trim();
}

export type AttachedRepoRef = { owner: string; name: string; fullName: string };

/** Groups file edits by repository fullName for multi-repo diff panels. */
export function groupEditsByRepo(
  files: AIFileEdit[],
  attachedRepos: AttachedRepoRef[]
): Map<string, { owner: string; name: string; fullName: string; files: AIFileEdit[] }> {
  const groups = new Map<string, { owner: string; name: string; fullName: string; files: AIFileEdit[] }>();
  for (const file of files) {
    let fullName = file.repo?.trim();
    if (!fullName && attachedRepos.length === 1) {
      fullName = attachedRepos[0].fullName;
    }
    if (!fullName) continue;
    const ref = attachedRepos.find((r) => r.fullName === fullName);
    if (!ref) continue;
    const existing = groups.get(fullName);
    if (existing) {
      existing.files.push(file);
    } else {
      groups.set(fullName, { owner: ref.owner, name: ref.name, fullName: ref.fullName, files: [file] });
    }
  }
  return groups;
}

/** Resolves the qualified path used for validation: owner/repo/relative/path */
export function resolveEditQualifiedPath(
  file: AIFileEdit,
  attachedRepos: AttachedRepoRef[]
): string | null {
  const path = file.file_path?.trim();
  if (!path) return null;
  const repo = file.repo?.trim() || (attachedRepos.length === 1 ? attachedRepos[0].fullName : "");
  if (!repo) return null;
  return `${repo}/${path}`;
}

// ── Branch name helpers ───────────────────────────────────────────────────────

export function generateBranchName(
  description: string,
  type: 'feat' | 'fix' | 'refactor' | 'chore' = 'feat'
): string {
  const date = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    .toLowerCase().replace(' ', '');
  const slug = description
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 40)
    .replace(/-$/, '');
  return `ai/${type}/${slug}-${date}`;
}

export function detectCommitType(files: AIFileEdit[]): 'feat' | 'fix' | 'refactor' | 'chore' {
  const desc = files.map(f => f.description.toLowerCase()).join(' ');
  if (desc.includes('fix') || desc.includes('bug') || desc.includes('error')) return 'fix';
  if (desc.includes('refactor') || desc.includes('clean') || desc.includes('rename')) return 'refactor';
  if (desc.includes('config') || desc.includes('chore')) return 'chore';
  return 'feat';
}

export function buildCommitMessage(files: AIFileEdit[]): string {
  const type = detectCommitType(files);
  if (files.length === 1) {
    const desc = files[0].description.replace(/^(feat|fix|refactor|chore):\s*/i, '').toLowerCase();
    return `${type}: ${desc}`;
  }
  const names = files.map(f => f.file_path.split('/').pop()).join(', ');
  return `${type}: AI suggested changes to ${names}`;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export async function createBranch(
  owner: string,
  repo: string,
  branchName: string,
  baseBranch?: string
): Promise<{ branchName: string; sha: string; base: string }> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/github/repos/${owner}/${repo}/create-branch`, {
    method: 'POST', headers,
    body: JSON.stringify({ branchName, baseBranch }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Failed to create branch: ${res.status}`);
  }
  return res.json();
}

export async function commitFiles(
  owner: string,
  repo: string,
  branch: string,
  files: Array<{ path: string; content: string }>,
  message: string
): Promise<CommitResult> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/github/repos/${owner}/${repo}/commit-files`, {
    method: 'POST', headers,
    body: JSON.stringify({ branch, message, files }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Failed to commit files: ${res.status}`);
  }
  return res.json();
}

export async function createPR(
  owner: string,
  repo: string,
  head: string,
  title: string,
  body: string,
  base?: string
): Promise<PRResult> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/github/repos/${owner}/${repo}/create-pr`, {
    method: 'POST', headers,
    body: JSON.stringify({ head, base, title, body }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Failed to create PR: ${res.status}`);
  }
  return res.json();
}

/** Push AI edits to GitHub with configurable branch/PR options. */
export async function pushAIEditToGitHub(
  owner: string,
  repo: string,
  editBlock: AIEditBlock,
  branchOptions: PushBranchOptions,
  onStep?: (step: 'branch' | 'commit' | 'pr') => void
): Promise<PushResult> {
  const headBranch = branchOptions.headBranch.trim();
  if (!headBranch) throw new Error('Branch name is required');

  const commitMessage = buildCommitMessage(editBlock.files);
  const filePayload = editBlock.files.map(f => ({ path: f.file_path, content: f.content }));

  if (branchOptions.mode === 'new_branch_pr') {
    onStep?.('branch');
    await createBranch(owner, repo, headBranch, branchOptions.baseBranch);
  } else if (branchOptions.mode === 'commit_only' && branchOptions.baseBranch) {
    // New branch name with explicit base — create then commit without PR.
    onStep?.('branch');
    await createBranch(owner, repo, headBranch, branchOptions.baseBranch);
  }

  onStep?.('commit');
  const commit = await commitFiles(owner, repo, headBranch, filePayload, commitMessage);

  const shouldOpenPr =
    branchOptions.mode === 'new_branch_pr' ||
    (branchOptions.mode === 'existing_branch' &&
      branchOptions.openPr &&
      branchOptions.prBase &&
      headBranch !== branchOptions.prBase);

  if (!shouldOpenPr) {
    return { branch: headBranch, commit };
  }

  onStep?.('pr');
  const prBody = [
    '## AI-Suggested Changes\n',
    'This PR was created by the AI assistant in Deeply Smart.\n',
    '### Files Changed\n',
    ...editBlock.files.map(f => `- **\`${f.file_path}\`** — ${f.description}`),
    `\n---\n> Commit: ${commit.commitSha}`,
  ].join('\n');

  const pr = await createPR(
    owner,
    repo,
    headBranch,
    commitMessage,
    prBody,
    branchOptions.prBase
  );
  return { branch: headBranch, commit, pr };
}
